/*
  /pages/wishlist: shows the Add All to Cart bar when at least one saved item can
  be bought, and runs it. The add itself is MNWishlist.addAllToCart in
  assets/wishlist-add-all.js, shared with the wishlist drawer.

  The React grid (src/wishlist) marks each buyable card data-available="true"
  once its product data has loaded. A MutationObserver follows that instead of
  the 500ms poll this file used to run for as long as the page was open.
*/
(() => {
  const BUYABLE = '[data-component="Wishlist"] .product-card[data-available="true"]';

  // Add All to Cart is switched on and off in Theme settings > Wishlist. While
  // it's off, sections/wishlist.liquid gives the section data-add-all-paused
  // (the text to show): the bar still shows, but the button is greyed out,
  // says when it'll work, and does nothing. Each card's own Add to cart still
  // works. The drawer follows the same setting (sections/wishlist-slideout.liquid).
  function pausedLabel() {
    const section = document.querySelector('[data-wishlist-section][data-add-all-paused]');
    return section ? section.dataset.addAllPaused : null;
  }

  function buyableEntries() {
    return Array.from(document.querySelectorAll(BUYABLE))
      .map((card) => ({
        epi: card.dataset.variantId,
        pid: card.dataset.productId,
        label: card.dataset.label,
      }))
      .filter((entry) => entry.epi);
  }

  // A list opened from someone's share link: adding to cart leaves it as it
  // is, and never touches the shopper's own saved items.
  function isSharedList() {
    return document.querySelector('[data-component="Wishlist"] [data-wishlist-shared]') !== null;
  }

  // wishlist.css shows and hides .addAllWishlistWrapper from these body classes.
  function syncAddAllBar() {
    const hasBuyable = document.querySelector(BUYABLE) !== null;
    document.body.classList.toggle('wishlist-add-all-available', hasBuyable);
    document.body.classList.toggle('wishlist-add-all-unavailable', !hasBuyable);
    // The bar is hidden until the line above shows it, so the button is
    // already disabled the first time a shopper sees it.
    const paused = pausedLabel();
    if (paused !== null) {
      document.querySelectorAll('#add-all-wishlist').forEach((button) => {
        button.disabled = true;
        // The label belongs to the React grid (src/wishlist/Wishlist.tsx).
        // Swapping its text here saves rebuilding the bundles: React never
        // rewrites it because its own copy doesn't change, and a remounted
        // button comes back through here via the MutationObserver below.
        const label = button.querySelector('span');
        if (label && paused && label.textContent !== paused) label.textContent = paused;
      });
    }
  }

  // The grid mutates in bursts while it loads; sync once per burst.
  let syncTimer = null;
  function queueSync() {
    if (syncTimer) return;
    syncTimer = setTimeout(() => {
      syncTimer = null;
      syncAddAllBar();
    }, 50);
  }

  function observe() {
    document.querySelectorAll('[data-wishlist-section]').forEach((section) => {
      if (section.dataset.addAllObserved) return;
      section.dataset.addAllObserved = 'true';
      new MutationObserver(queueSync).observe(section, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-available'],
      });
    });
    syncAddAllBar();
  }

  function statusFor(button) {
    const section = button.closest('[data-wishlist-section]') || document;
    return section.querySelector('[data-wishlist-add-all-status]');
  }

  function showStatus(statusEl, message, withCartLink) {
    if (!statusEl) return;
    statusEl.textContent = message;
    if (withCartLink) {
      const viewCart = document.createElement('button');
      viewCart.type = 'button';
      viewCart.className = 'link wishlist-add-all-status__cart';
      viewCart.textContent = 'View cart';
      // cart:open (cart-drawer.js) re-reads the cart, so a line the post-add
      // chores added since (auto-donation) shows too.
      viewCart.addEventListener('click', () => document.dispatchEvent(new CustomEvent('cart:open')));
      statusEl.append(' ', viewCart);
    }
    statusEl.hidden = !message;
    if (message) statusEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  // Adds entries to the cart and reports back: the cart drawer when everything
  // went in, otherwise the status line with the cart one tap away. Shared by
  // Add All and each card's own Add to cart (src/wishlist/WishlistCard.tsx).
  async function addToCart(entries, button) {
    if (!entries.length || !window.MNWishlist) return null;

    const statusEl = statusFor(button);
    if (statusEl) {
      statusEl.hidden = true;
      statusEl.textContent = '';
    }

    const result = await window.MNWishlist.addAllToCart(entries, { removeFromWishlist: !isSharedList() });

    // The grid still thinks refused items are buyable. Take them out of the
    // next Add All so it doesn't retry what Shopify just refused.
    result.failed.forEach((entry) => {
      const card = document.querySelector(
        `[data-component="Wishlist"] .product-card[data-variant-id="${entry.epi}"]`
      );
      if (card) card.dataset.available = 'false';
    });

    const message = window.MNWishlist.describe(result);
    if (!message) {
      window.MNWishlist.showCart(result.cart);
      return result;
    }
    // Something was refused: say so here, with the cart one tap away, instead
    // of covering the message with the drawer.
    if (result.added.length) window.MNWishlist.showCart(result.cart, { open: false });
    showStatus(statusEl, message, result.added.length > 0);
    return result;
  }

  async function onAddAll(button) {
    const entries = buyableEntries();
    if (!entries.length) return;

    button.disabled = true;
    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');

    await addToCart(entries, button);

    button.disabled = false;
    button.classList.remove('loading');
    button.removeAttribute('aria-busy');
  }

  window.MNWishlistPage = { addToCart };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('#add-all-wishlist');
    if (!button || button.disabled) return;
    event.preventDefault();
    onAddAll(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
  document.addEventListener('shopify:section:load', observe);
})();
